import { useCallback, useEffect, useRef, useState } from 'react';
import { GetAdminChangelogRecipientsAPI } from '../../../../api/AdminAPI';

const DEFAULT_PAGINATION = { total: 0, page: 1, limit: 20, pages: 1 };
const DEFAULT_FILTERS = { status: 'all', opened: '', clicked: '' };
const SEARCH_DEBOUNCE_MS = 400;

/**
 * useEditionRecipients
 *
 * Destinatários de um envio (admin/changelog/recipients.php) com busca, filtros de
 * situação/abriu/clicou e paginação. Carrega só enquanto o diálogo está aberto e volta
 * aos filtros padrão a cada envio aberto.
 *
 * @param {number|null} editionId - envio aberto no diálogo (null = fechado)
 */
function useEditionRecipients(editionId) {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [searchInput, setSearchInputState] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [recipients, setRecipients] = useState([]);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const searchTimer = useRef(null);

  useEffect(() => {
    setFilters(DEFAULT_FILTERS);
    setSearchInputState('');
    setQ('');
    setPage(1);
    setRecipients([]);
    setPagination(DEFAULT_PAGINATION);
  }, [editionId]);

  useEffect(() => () => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
  }, []);

  const setSearchInput = useCallback((value) => {
    setSearchInputState(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setQ(value.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
  }, []);

  const setFilter = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  useEffect(() => {
    if (editionId === null || editionId === undefined) return undefined;
    let active = true;
    setIsLoading(true);
    setError(null);
    GetAdminChangelogRecipientsAPI(
      { edition_id: editionId, q, ...filters, page, limit: DEFAULT_PAGINATION.limit },
      (response) => {
        if (!active) return;
        if (!response || response.success === false) {
          setError(response?.error || 'Erro ao carregar os destinatários. Tente novamente.');
          setIsLoading(false);
          return;
        }
        setRecipients(Array.isArray(response.recipients) ? response.recipients : []);
        setPagination(response.pagination || { ...DEFAULT_PAGINATION, page });
        setIsLoading(false);
      }
    );
    return () => { active = false; };
  }, [editionId, q, filters, page]);

  const hasActiveFilters = Boolean(q || filters.status !== 'all' || filters.opened || filters.clicked);

  return {
    recipients, pagination, isLoading, error,
    filters, setFilter, searchInput, setSearchInput, setPage, hasActiveFilters,
  };
}

export default useEditionRecipients;
